// Mandatory confirmation blocks for the product page (snippets/product-confirm-checkbox.liquid):
// a tick box, optionally with a required text field (vehicle details).
// Blocks Add to cart, Buy it now and the bundle button until every block on the page is completed.
if (!customElements.get('product-confirm-checkbox')) {
  customElements.define(
    'product-confirm-checkbox',
    class ProductConfirmCheckbox extends HTMLElement {
      static instancesFor(formId) {
        return Array.from(document.querySelectorAll(`product-confirm-checkbox[data-form="${formId}"]`));
      }

      // Shows errors on every incomplete block for the form; returns false if any were incomplete
      static validate(formId) {
        const incomplete = ProductConfirmCheckbox.instancesFor(formId).filter((el) => !el.complete);
        incomplete.forEach((el, index) => el.showError(index === 0));
        return incomplete.length === 0;
      }

      connectedCallback() {
        this.checkbox = this.querySelector('input[type="checkbox"]');
        this.textInput = this.querySelector('.product-confirm__input');
        this.error = this.querySelector('.product-confirm__error');
        this.form = document.getElementById(this.dataset.form);
        this.productForm = this.form && this.form.closest('product-form');
        this.submitButton = this.productForm && this.productForm.querySelector('.product-form__submit');

        this.checkbox.addEventListener('change', () => this.update());
        if (this.textInput) this.textInput.addEventListener('input', () => this.update());

        // One set of listeners per product form, shared by all blocks on it.
        // Capture phase on the wrapper runs before product-form.js's own submit handler on the form.
        if (this.productForm && !this.productForm.dataset.confirmListening) {
          this.productForm.dataset.confirmListening = 'true';
          const formId = this.dataset.form;
          this.productForm.addEventListener(
            'submit',
            (event) => {
              if (ProductConfirmCheckbox.validate(formId)) return;
              event.preventDefault();
              event.stopPropagation();
            },
            true
          );
          // Add to cart and Buy it now can't be clicked while locked (see CSS), so the click lands on their wrapper
          this.productForm.addEventListener('click', (event) => {
            if (!event.target.closest('.shopify-payment-button, .product-form__buttons')) return;
            if (!ProductConfirmCheckbox.validate(formId)) event.preventDefault();
          });
        }

        // Apps (e.g. cart upsell apps) can add to cart from their own click handlers, so the
        // button itself is disabled until completed. Re-apply if variant changes re-enable it.
        if (this.submitButton) {
          this.observer = new MutationObserver(() => this.syncForm());
          this.observer.observe(this.submitButton, { attributes: true, attributeFilter: ['disabled'] });
        }

        this.update();
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        this.syncForm();
      }

      get complete() {
        return this.checkbox.checked && (!this.textInput || this.textInput.value.trim() !== '');
      }

      update() {
        if (this.complete) this.hideError();
        else if (this.textInput && this.textInput.value.trim() !== '') this.textInput.classList.remove('is-invalid');
        this.syncForm();
      }

      // Locks the form's buttons unless every block on it is complete
      syncForm() {
        const allComplete = ProductConfirmCheckbox.instancesFor(this.dataset.form).every((el) => el.complete);
        if (this.productForm) this.productForm.classList.toggle('product-form--needs-confirm', !allComplete);

        const button = this.submitButton;
        if (!button) return;

        if (!allComplete) {
          if (!button.hasAttribute('disabled')) {
            button.setAttribute('disabled', 'disabled');
            button.dataset.confirmLocked = 'true';
          }
          return;
        }

        if (button.dataset.confirmLocked) {
          delete button.dataset.confirmLocked;
          // Leave it disabled if the theme marked the variant sold out / unavailable meanwhile
          const label = (button.querySelector('span') || button).textContent.trim();
          const strings = window.variantStrings || {};
          if (label !== strings.soldOut && label !== strings.unavailable) button.removeAttribute('disabled');
        }
      }

      showError(scrollTo = true) {
        const needsText = this.textInput && this.textInput.value.trim() === '';
        this.error.hidden = false;
        this.classList.add('is-invalid');
        if (this.textInput) this.textInput.classList.toggle('is-invalid', needsText);
        if (!scrollTo) return;
        this.scrollIntoView({ behavior: 'smooth', block: 'center' });
        (needsText ? this.textInput : this.checkbox).focus({ preventScroll: true });
      }

      hideError() {
        this.error.hidden = true;
        this.classList.remove('is-invalid');
        if (this.textInput) this.textInput.classList.remove('is-invalid');
      }
    }
  );

  // Other add-to-cart buttons on the same page (e.g. the bundle section)
  document.addEventListener(
    'click',
    (event) => {
      if (!event.target.closest || !event.target.closest('.ss-bundle-cta')) return;
      const incomplete = Array.from(document.querySelectorAll('product-confirm-checkbox')).filter((el) => !el.complete);
      if (!incomplete.length) return;
      event.preventDefault();
      event.stopPropagation();
      incomplete.forEach((el, index) => el.showError(index === 0));
    },
    true
  );
}
